import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  catalogPlugin,
  CatalogTable,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { DurationPickerFieldExtension, UserInfoInputFieldExtension } from '@internal/plugin-scaffolder-field-extensions';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue, LightBox } from '@backstage/plugin-techdocs-module-addons-contrib';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';

import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis, kcOIDCAuthApiRef } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage
} from '@backstage/core-components';
import { createApp } from '@backstage/frontend-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { UnifiedThemeProvider } from '@backstage/theme';
import { customTheme } from './theme/customTheme';
import { VisitListener } from '@backstage/plugin-home';
import { CustomCard } from './components/template/CustomCard';
import {
  apiDocsTranslationModule,
  catalogReactTranslationModule,
  scaffolderTranslationModule,
  searchTranslationModule,
  coreComponentsTranslationModule,
  userSettingsTranslationModule,
} from './translations/pt-BR';
import {
  convertLegacyAppOptions,
  convertLegacyAppRoot,
  convertLegacyRouteRef,
  convertLegacyRouteRefs,
} from '@backstage/core-compat-api';
import './styles.css';
import { TemplateTypes, getTemplateType } from './constants/templateTypes';

import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { HomePage } from './pages/home';
import { HomePageRedirect } from './pages/home-redirect';
import { CustomCatalogIndexPage } from './pages/catalog';

const convertedOptionsModule = convertLegacyAppOptions({
  apis,
  plugins: [
    catalogPlugin
  ],
  themes: [{
    id: 'custom-theme',
    title: 'Custom Theme',
    variant: 'light',
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={customTheme} children={children} />
    ),
  }],
  components: {
    SignInPage: props => {
      const appConfigScript = document.querySelector('script[type="backstage.io/config"]');
      const appConfigs = appConfigScript ? JSON.parse(appConfigScript.textContent ?? '[]') : [];
      const redirectUrl = appConfigs.flatMap((c: any) => [c.data?.app?.redirectHomeOlimpiaUrl]).find(Boolean);

      return (!!redirectUrl) 
      ? <HomePageRedirect redirectUrl={redirectUrl} />
      : (<SignInPage {...props} auto providers={[{
        apiRef: kcOIDCAuthApiRef,
        id: "keycloak",
        message: "Bem-vindo ao Portal de Inteligência Artificial. Clique em Entrar para iniciar sua sessão e explorar nossos serviços.",
        title: "PORTAL OLÍMPIA"
      }]} />);
    },
  },
});


const columnsForAllKinds = () => [
  CatalogTable.columns.createNameColumn(),
  CatalogTable.columns.createMetadataDescriptionColumn(),
  CatalogTable.columns.createOwnerColumn(),
  CatalogTable.columns.createSpecTypeColumn(),
  CatalogTable.columns.createSpecLifecycleColumn(),
  CatalogTable.columns.createTagsColumn()
];


const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomePage />} />
    <Route path="/catalog" element={<CustomCatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name/*"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>

    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
        <LightBox />
        <Mermaid />
      </TechDocsAddons>
    </Route>
    <Route path="/create"
      element={
        <ScaffolderPage
          groups={[{
            title: 'Aplicações',
            filter: template =>
              template.spec.type === TemplateTypes.Aplicacao,
          },
          {
            title: 'Serviços',
            filter: template =>
              template.spec.type === TemplateTypes.Servico,
          },
          {
            title: 'APIs',
            filter: template =>
              template.spec.type === TemplateTypes.API,
          },
          {
            title: 'Credenciais',
            filter: template =>
              template.spec.type === TemplateTypes.Credencial,
          },
          {
            title: 'Outros',
            filter: template =>
              getTemplateType(template.spec.type) === TemplateTypes.Outros,
          }]}
          components={{ TemplateCardComponent: CustomCard }}
        />} >  
        <ScaffolderFieldExtensions>
          <UserInfoInputFieldExtension />
          <DurationPickerFieldExtension />
        </ScaffolderFieldExtensions>
      </Route>
    <Route path="/api-docs" element={<ApiExplorerPage columns={columnsForAllKinds()} />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
  </FlatRoutes>
);


const convertedRootFeatures = convertLegacyAppRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <VisitListener />
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);

const app = createApp({
  features: [
    convertedOptionsModule,
    ...convertedRootFeatures,
    catalogReactTranslationModule,
    scaffolderTranslationModule,
    searchTranslationModule,
    userSettingsTranslationModule,
    apiDocsTranslationModule,
    coreComponentsTranslationModule,
  ],
  bindRoutes({ bind }) {
    bind(convertLegacyRouteRefs(catalogPlugin.externalRoutes), {
      createComponent: convertLegacyRouteRef(scaffolderPlugin.routes.root),
      viewTechDoc: convertLegacyRouteRef(techdocsPlugin.routes.docRoot),
      createFromTemplate: convertLegacyRouteRef(scaffolderPlugin.routes.selectedTemplate),
    });
    bind(convertLegacyRouteRefs(apiDocsPlugin.externalRoutes), {
      registerApi: convertLegacyRouteRef(catalogImportPlugin.routes.importPage),
    });
    bind(convertLegacyRouteRefs(scaffolderPlugin.externalRoutes), {
      registerComponent: convertLegacyRouteRef(catalogImportPlugin.routes.importPage),
      viewTechDoc: convertLegacyRouteRef(techdocsPlugin.routes.docRoot),
    });
    bind(convertLegacyRouteRefs(orgPlugin.externalRoutes), {
      catalogIndex: convertLegacyRouteRef(catalogPlugin.routes.catalogIndex),
    });
  },
})

export default app.createRoot();
